import React, { useState, useEffect, useRef } from 'react';
import { BarChart2, Percent, TrendingUp, TrendingDown, RefreshCcw, Clock, Users, DollarSign, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight, Zap, Star, Trophy, List, Info, Eye, Copy, Check, ExternalLink } from 'lucide-react';
import { getTokenDetailedStats, getTokenHoldersPaginated, getTokenTopHolders, getTokenFirstBuyers } from '../../services/solanatrackerService';
import TokenSearchModal from '../../components/TokenSearchModal';
import { Token } from '../../types/supabase';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';

// Define tab types
type TabType = 'stats' | 'holders' | 'topHolders' | 'firstBuyers';

interface TokenStatsProps {
  onClose?: () => void;
  isWidget?: boolean;
  selectedToken?: Token;
}

const TokenStats: React.FC<TokenStatsProps> = ({
  onClose,
  isWidget = false,
  selectedToken
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenStats, setTokenStats] = useState<any>(null);
  const [timeframe, setTimeframe] = useState<string>('1h');
  
  // New states for tabs and additional data
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const [holdersData, setHoldersData] = useState<any>(null);
  const [topHoldersData, setTopHoldersData] = useState<any>(null);
  const [firstBuyersData, setFirstBuyersData] = useState<any>(null);
  const [isLoadingHolders, setIsLoadingHolders] = useState(false);
  const [isLoadingTopHolders, setIsLoadingTopHolders] = useState(false);
  const [isLoadingFirstBuyers, setIsLoadingFirstBuyers] = useState(false);
  const [holdersPage, setHoldersPage] = useState(1);
  const [holdersLimit] = useState(50);
  
  // Token search state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  
  // Use the local token state if available, otherwise use the prop
  const effectiveToken = localSelectedToken || selectedToken;
  
  // Update local state when prop changes
  useEffect(() => {
    if (selectedToken) {
      setLocalSelectedToken(selectedToken);
    }
  }, [selectedToken]);
  
  // Available timeframes
  const timeframes = ['1m', '5m', '15m', '1h', '4h', '24h'];
  
  // Add copy function and state for copied addresses
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  
  // Function to copy wallet address to clipboard
  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000); // Reset after 2 seconds
  };
  
  // Update the style for scrollable containers
  const scrollableContainerStyle = `
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
      overflow-y: auto;
      max-height: 100%;
      height: 100%;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
    
    .table-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
      min-height: 0;
      height: 100%;
    }
    
    .scrollable-content {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }
  `;
  
  // Fetch token stats when token is available
  useEffect(() => {
    const fetchTokenStats = async () => {
      if (!effectiveToken?.mint) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Use the service instead of direct API client
        const result = await getTokenDetailedStats(effectiveToken.mint);
        
        if (result.status === 'success' && result.data) {
          setTokenStats(result.data);
        } else {
          setError(result.error || 'Failed to fetch token statistics');
        }
      } catch (err) {
        console.error('Error fetching token stats:', err);
        setError('Failed to fetch token statistics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokenStats();
  }, [effectiveToken?.mint]);
  
  // Fetch holders data when tab is selected
  useEffect(() => {
    if (activeTab === 'holders' && effectiveToken?.mint && !holdersData) {
      fetchHolders();
    }
  }, [activeTab, effectiveToken?.mint, holdersData]);
  
  // Fetch top holders data when tab is selected
  useEffect(() => {
    if (activeTab === 'topHolders' && effectiveToken?.mint && !topHoldersData) {
      fetchTopHolders();
    }
  }, [activeTab, effectiveToken?.mint, topHoldersData]);
  
  // Fetch first buyers data when tab is selected
  useEffect(() => {
    if (activeTab === 'firstBuyers' && effectiveToken?.mint && !firstBuyersData) {
      fetchFirstBuyers();
    }
  }, [activeTab, effectiveToken?.mint, firstBuyersData]);
  
  // Fetch token holders with pagination
  const fetchHolders = async () => {
    if (!effectiveToken?.mint) return;
    
    setIsLoadingHolders(true);
    
    try {
      const result = await getTokenHoldersPaginated(effectiveToken.mint, holdersPage, holdersLimit);
      
      if (result.status === 'success' && result.data) {
        setHoldersData(result.data);
      } else {
        console.error('Failed to fetch token holders:', result.error);
      }
    } catch (err) {
      console.error('Error fetching token holders:', err);
    } finally {
      setIsLoadingHolders(false);
    }
  };
  
  // Fetch top holders
  const fetchTopHolders = async () => {
    if (!effectiveToken?.mint) return;
    
    setIsLoadingTopHolders(true);
    
    try {
      const result = await getTokenTopHolders(effectiveToken.mint);
      
      if (result.status === 'success' && result.data) {
        setTopHoldersData(result.data);
      } else {
        console.error('Failed to fetch top holders:', result.error);
      }
    } catch (err) {
      console.error('Error fetching top holders:', err);
    } finally {
      setIsLoadingTopHolders(false);
    }
  };
  
  // Fetch first buyers
  const fetchFirstBuyers = async () => {
    if (!effectiveToken?.mint) return;
    
    setIsLoadingFirstBuyers(true);
    
    try {
      const result = await getTokenFirstBuyers(effectiveToken.mint);
      
      if (result.status === 'success' && result.data) {
        setFirstBuyersData(result.data);
      } else {
        console.error('Failed to fetch first buyers:', result.error);
      }
    } catch (err) {
      console.error('Error fetching first buyers:', err);
    } finally {
      setIsLoadingFirstBuyers(false);
    }
  };
  
  // Handle pagination for holders
  const goToNextPage = () => {
    if (holdersData && holdersData.accounts.length === holdersLimit) {
      setHoldersPage(prev => prev + 1);
      setHoldersData(null); // Reset data to trigger a new fetch
    }
  };
  
  const goToPrevPage = () => {
    if (holdersPage > 1) {
      setHoldersPage(prev => prev - 1);
      setHoldersData(null); // Reset data to trigger a new fetch
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    if (Math.abs(amount) >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(2)}M`;
    } else if (Math.abs(amount) >= 1_000) {
      return `$${(amount / 1_000).toFixed(2)}K`;
    } else if (Math.abs(amount) < 0.01 && amount !== 0) {
      return `$${amount.toFixed(6)}`;
    } else {
      return `$${amount.toFixed(2)}`;
    }
  };

  // Format number with K/M/B suffix
  const formatNumber = (num: number): string => {
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(2)}B`;
    } else if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(2)}M`;
    } else if (num >= 1_000) {
      return `${(num / 1_000).toFixed(2)}K`;
    } else {
      return num.toString();
    }
  };
  
  // Format percentage
  const formatPercentage = (num: number): string => {
    return `${num.toFixed(2)}%`;
  };
  
  // Format date from timestamp
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Format wallet address
  const formatAddress = (address: string): string => {
    if (!address || address.length < 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Refresh data
  const handleRefresh = async () => {
    if (!effectiveToken?.mint) return;
    
    switch (activeTab) {
      case 'stats':
        setIsLoading(true);
        setError(null);
        
        try {
          const result = await getTokenDetailedStats(effectiveToken.mint);
          
          if (result.status === 'success' && result.data) {
            setTokenStats(result.data);
          } else {
            setError(result.error || 'Failed to refresh token statistics');
          }
        } catch (err) {
          console.error('Error refreshing token stats:', err);
          setError('Failed to refresh token statistics');
        } finally {
          setIsLoading(false);
        }
        break;
        
      case 'holders':
        setHoldersData(null);
        fetchHolders();
        break;
        
      case 'topHolders':
        setTopHoldersData(null);
        fetchTopHolders();
        break;
        
      case 'firstBuyers':
        setFirstBuyersData(null);
        fetchFirstBuyers();
        break;
    }
  };

  // Get the current stats for the selected timeframe
  const currentStats = tokenStats?.[timeframe];
  
  // Render token stats content
  const renderStatsContent = () => {
    if (isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCcw size={20} className="animate-spin text-[#24c35b] mr-2" />
          <span className="text-gray-600">Loading token statistics...</span>
        </div>
      );
    }
    
    if (!tokenStats || !currentStats) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <span>No statistics available for {effectiveToken?.name}</span>
        </div>
      );
    }
    
    return (
      <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-0">
        {/* Price and Change */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <DollarSign size={14} className="text-[#24c35b] mr-1" />
            <span className="text-sm font-medium text-black">Price Info</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Current Price</div>
              <div className="text-sm font-semibold text-black">
                ${currentStats.price.toFixed(6)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Price Change ({timeframe})</div>
              <div className={`text-sm font-semibold flex items-center ${
                currentStats.priceChangePercentage >= 0 ? 'text-[#24c35b]' : 'text-red-500'
              }`}>
                {currentStats.priceChangePercentage >= 0 ? (
                  <ArrowUp size={12} className="mr-1" />
                ) : (
                  <ArrowDown size={12} className="mr-1" />
                )}
                {currentStats.priceChangePercentage.toFixed(2)}%
              </div>
            </div>
          </div>
        </div>
        
        {/* Volume */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <TrendingUp size={14} className="text-[#24c35b] mr-1" />
            <span className="text-sm font-medium text-black">Volume ({timeframe})</span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Buy Volume</div>
              <div className="text-sm font-semibold text-[#24c35b]">
                {formatCurrency(currentStats.volume.buys)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Sell Volume</div>
              <div className="text-sm font-semibold text-red-500">
                {formatCurrency(currentStats.volume.sells)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Total Volume</div>
              <div className="text-sm font-semibold text-black">
                {formatCurrency(currentStats.volume.total)}
              </div>
            </div>
          </div>
        </div>
        
        {/* Transactions */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Clock size={14} className="text-[#24c35b] mr-1" />
            <span className="text-sm font-medium text-black">Transactions ({timeframe})</span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Buy Txns</div>
              <div className="text-sm font-semibold text-[#24c35b]">
                {formatNumber(currentStats.buys)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Sell Txns</div>
              <div className="text-sm font-semibold text-red-500">
                {formatNumber(currentStats.sells)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Total Txns</div>
              <div className="text-sm font-semibold text-black">
                {formatNumber(currentStats.transactions)}
              </div>
            </div>
          </div>
        </div>
        
        {/* Wallets */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Users size={14} className="text-[#24c35b] mr-1" />
            <span className="text-sm font-medium text-black">Wallet Activity ({timeframe})</span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Buyers</div>
              <div className="text-sm font-semibold text-[#24c35b]">
                {formatNumber(currentStats.buyers)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Sellers</div>
              <div className="text-sm font-semibold text-red-500">
                {formatNumber(currentStats.sellers)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Unique Wallets</div>
              <div className="text-sm font-semibold text-black">
                {formatNumber(currentStats.wallets)}
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Stats */}
        <div>
          <div className="flex items-center mb-2">
            <Percent size={14} className="text-[#24c35b] mr-1" />
            <span className="text-sm font-medium text-black">Key Metrics</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Buy/Sell Ratio</div>
              <div className="text-sm font-semibold text-black">
                {(currentStats.buys / (currentStats.sells || 1)).toFixed(2)}
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-1">Avg. Transaction</div>
              <div className="text-sm font-semibold text-black">
                {formatCurrency(currentStats.volume.total / currentStats.transactions)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render token holders content
  const renderHoldersContent = () => {
    if (isLoadingHolders) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCcw size={20} className="animate-spin text-[#24c35b] mr-2" />
          <span className="text-white/70">Loading token holders...</span>
        </div>
      );
    }
    
    if (!holdersData || !holdersData.accounts || holdersData.accounts.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <span>No holder data available for {effectiveToken?.name}</span>
        </div>
      );
    }
    
    return (
      <div className="flex-1 flex flex-col table-container">
        <div className="p-3 border-b border-white/10 flex justify-between items-center">
          <div className="text-xs text-gray-400">
            Total Holders: <span className="text-white">{formatNumber(holdersData.total)}</span>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={goToPrevPage} 
              disabled={holdersPage === 1}
              className={`p-1 rounded ${holdersPage === 1 ? 'text-gray-500' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-white">Page {holdersPage}</span>
            <button 
              onClick={goToNextPage} 
              disabled={holdersData.accounts.length < holdersLimit}
              className={`p-1 rounded ${holdersData.accounts.length < holdersLimit ? 'text-gray-500' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        
        <div className="scrollable-content custom-scrollbar">
          <table className="w-full">
            <thead className="sticky top-0 bg-white backdrop-blur-sm">
              <tr className="text-left text-xs text-[#24c35b] border-b border-gray-200">
                <th className="p-2">#</th>
                <th className="p-2">Wallet</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-right">Value</th>
                <th className="p-2 text-right">% Supply</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {holdersData.accounts.map((holder: any, index: number) => (
                <tr key={holder.wallet} className="text-xs hover:bg-gray-50">
                  <td className="p-2 text-black">{(holdersPage - 1) * holdersLimit + index + 1}</td>
                  <td className="p-2 text-black">
                    <div className="flex items-center">
                      <span>{formatAddress(holder.wallet)}</span>
                      <button
                        onClick={() => copyToClipboard(holder.wallet)}
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress === holder.wallet ? 
                          <Check size={12} className="text-green-500" /> : 
                          <Copy size={12} />
                        }
                      </button>
                      <a
                        href={`https://solscan.io/account/${holder.wallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="View on Solscan"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </td>
                  <td className="p-2 text-right text-black">{formatNumber(holder.amount)}</td>
                  <td className="p-2 text-right text-black">
                    {holder.value && holder.value.usd ? formatCurrency(holder.value.usd) : '-'}
                  </td>
                  <td className="p-2 text-right text-black">{formatPercentage(holder.percentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render top holders content
  const renderTopHoldersContent = () => {
    if (isLoadingTopHolders) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCcw size={20} className="animate-spin text-[#24c35b] mr-2" />
          <span className="text-white/70">Loading top token holders...</span>
        </div>
      );
    }
    
    if (!topHoldersData || !Array.isArray(topHoldersData) || topHoldersData.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <span>No top holder data available for {effectiveToken?.name}</span>
        </div>
      );
    }
    
    return (
      <div className="flex-1 flex flex-col table-container">
        <div className="p-3 border-b border-white/10">
          <div className="text-xs text-gray-400">
            Top Holders: <span className="text-white">{topHoldersData.length}</span>
          </div>
        </div>
        
        <div className="scrollable-content custom-scrollbar">
          <table className="w-full">
            <thead className="sticky top-0 bg-white backdrop-blur-sm">
              <tr className="text-left text-xs text-[#24c35b] border-b border-gray-200">
                <th className="p-2">Rank</th>
                <th className="p-2">Address</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-right">Value</th>
                <th className="p-2 text-right">% Supply</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topHoldersData.map((holder: any, index: number) => (
                <tr key={holder.address} className="text-xs hover:bg-gray-50">
                  <td className="p-2 text-black">{index + 1}</td>
                  <td className="p-2 text-black">
                    <div className="flex items-center">
                      <span>{formatAddress(holder.address)}</span>
                      <button
                        onClick={() => copyToClipboard(holder.address)}
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress === holder.address ? 
                          <Check size={12} className="text-green-500" /> : 
                          <Copy size={12} />
                        }
                      </button>
                      <a
                        href={`https://solscan.io/account/${holder.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="View on Solscan"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </td>
                  <td className="p-2 text-right text-black">{formatNumber(holder.amount)}</td>
                  <td className="p-2 text-right text-black">
                    {holder.value && holder.value.usd ? formatCurrency(holder.value.usd) : '-'}
                  </td>
                  <td className="p-2 text-right text-black">{formatPercentage(holder.percentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render first buyers content
  const renderFirstBuyersContent = () => {
    if (isLoadingFirstBuyers) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCcw size={20} className="animate-spin text-[#24c35b] mr-2" />
          <span className="text-white/70">Loading first buyers...</span>
        </div>
      );
    }
    
    if (!firstBuyersData || !Array.isArray(firstBuyersData) || firstBuyersData.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <span>No first buyers data available for {effectiveToken?.name}</span>
        </div>
      );
    }
    
    return (
      <div className="flex-1 flex flex-col table-container">
        <div className="p-3 border-b border-white/10">
          <div className="text-xs text-gray-400">
            First Buyers (Snipers): <span className="text-white">{firstBuyersData.length}</span>
          </div>
        </div>
        
        <div className="scrollable-content custom-scrollbar">
          <table className="w-full">
            <thead className="sticky top-0 bg-white backdrop-blur-sm">
              <tr className="text-left text-xs text-[#24c35b] border-b border-gray-200">
                <th className="p-2">#</th>
                <th className="p-2">Wallet</th>
                <th className="p-2">First Buy Time</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-right">Current Value</th>
                <th className="p-2 text-right">P&L</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {firstBuyersData.map((buyer: any, index: number) => (
                <tr key={buyer.wallet} className="text-xs hover:bg-gray-50">
                  <td className="p-2 text-black">{index + 1}</td>
                  <td className="p-2 text-black">
                    <div className="flex items-center">
                      <span>{formatAddress(buyer.wallet)}</span>
                      <button
                        onClick={() => copyToClipboard(buyer.wallet)}
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress === buyer.wallet ? 
                          <Check size={12} className="text-green-500" /> : 
                          <Copy size={12} />
                        }
                      </button>
                      <a
                        href={`https://solscan.io/account/${buyer.wallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1.5 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                        title="View on Solscan"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </td>
                  <td className="p-2 text-black">{formatDate(buyer.first_buy_time)}</td>
                  <td className="p-2 text-right text-black">{formatNumber(buyer.first_buy.amount)}</td>
                  <td className="p-2 text-right text-black">{formatCurrency(buyer.current_value)}</td>
                  <td className={`p-2 text-right ${buyer.total >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {formatCurrency(buyer.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render tab content based on active tab
  const renderTabContent = () => {
    if (!effectiveToken) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <TrendingUp size={40} className="mb-4 opacity-20" />
          <p className="text-lg mb-2">No token selected</p>
          <button
            onClick={() => setIsSearchModalOpen(true)}
            className="mt-4 px-4 py-2 bg-[#24c35b] hover:bg-[#1eaa4e] text-white rounded-lg text-sm transition-colors flex items-center shadow-sm"
          >
            <Search size={14} className="mr-2" />
            Search for a token
          </button>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="flex-1 flex items-center justify-center text-red-500">
          <span>{error}</span>
        </div>
      );
    }
    
    switch (activeTab) {
      case 'stats':
        return renderStatsContent();
      case 'holders':
        return renderHoldersContent();
      case 'topHolders':
        return renderTopHoldersContent();
      case 'firstBuyers':
        return renderFirstBuyersContent();
      default:
        return null;
    }
  };

  const content = (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="token-stats">
      <style>{globalWidgetTextStyles}</style>
      <style>{scrollableContainerStyle}</style>
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
          <h2 className="text-lg font-bold text-black">Token Stats</h2>
        </div>
        
        <div className="flex items-center gap-2">
          {effectiveToken && (
            <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">{effectiveToken.name} ({effectiveToken.symbol})</span>
          )}
          
          {/* Always show search button */}
          <button
            ref={searchButtonRef}
            onClick={() => setIsSearchModalOpen(true)}
            className="p-2 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 text-[#24c35b] transition-colors flex items-center border border-[#24c35b]/20"
          >
            <Search size={14} className="mr-1" />
            <span className="text-xs">{effectiveToken ? 'Change' : 'Search Token'}</span>
          </button>
          
          {/* Show refresh button only when a token is selected */}
          {effectiveToken && (
            <button 
              onClick={handleRefresh} 
              className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-black transition-colors"
              disabled={isLoading || isLoadingHolders || isLoadingTopHolders || isLoadingFirstBuyers}
            >
              <RefreshCcw size={14} className={isLoading || isLoadingHolders || isLoadingTopHolders || isLoadingFirstBuyers ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </div>
      
      {/* Tab Navigation */}
      {effectiveToken && (
        <div className="flex bg-gray-50 border-b border-gray-200">
          {[
            { key: 'stats', label: 'Stats', icon: Info },
            { key: 'holders', label: 'Holders', icon: Users },
            { key: 'topHolders', label: 'Top Holders', icon: Trophy },
            { key: 'firstBuyers', label: 'First Buyers', icon: Zap }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as TabType)}
              className={`flex-1 py-3 px-2 text-xs font-medium transition-all ${
                activeTab === key
                  ? 'bg-white text-[#24c35b] border-b-2 border-[#24c35b] shadow-sm'
                  : 'text-gray-600 hover:text-black hover:bg-gray-100'
              }`}
            >
              <Icon size={14} className="mr-1 inline" />
              {label}
            </button>
          ))}
        </div>
      )}
      
      {/* Timeframe selector - only show if token is selected and in stats tab */}
      {effectiveToken && activeTab === 'stats' && (
        <div className="flex items-center space-x-1 p-3 bg-gray-50 border-b border-gray-200 overflow-x-auto no-scrollbar">
          {timeframes.map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                timeframe === tf
                  ? 'bg-[#24c35b] text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#24c35b] hover:text-black'
              }`}
            >
              {tf.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      
      {/* Content */}
      <div className="flex-grow overflow-y-auto px-3 py-3" style={{ background: 'white', borderRadius: 0, overflowX: 'visible' }}>
        {renderTabContent()}
      </div>
      
      {/* Token Search Modal */}
      <TokenSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectToken={(token) => {
          setLocalSelectedToken(token);
          setIsSearchModalOpen(false);
          // Reset all data when token changes
          setTokenStats(null);
          setHoldersData(null);
          setTopHoldersData(null);
          setFirstBuyersData(null);
          setHoldersPage(1);
        }}
        referenceElement={searchButtonRef.current}
      />
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart2 size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Token Stats</h2>
        </div>
        <div className="flex items-center gap-2">
          {!effectiveToken && (
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="rounded bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] transition-colors px-2 py-1 text-xs flex items-center"
            >
              <Search size={12} className="mr-1" />
              Search Token
            </button>
          )}
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              &times;
            </button>
          )}
        </div>
      </div>
      
      {content}
    </div>
  );
};

// Export a widget creator function for the canvas
export const createTokenStatsWidget = (selectedToken?: Token) => {
  const uniqueKey = `stats-${selectedToken?.mint || 'default'}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  return {
    type: 'token-stats',
    title: 'Token Stats',
    content: <TokenStats 
      key={uniqueKey}
      isWidget={true} 
      selectedToken={selectedToken} 
    />,
    size: { width: 600, height: 390 },
    contentStyle: { 
      padding: 0,
      overflow: 'hidden' 
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default TokenStats; 